@let detail = detail$ | async;

<div class="viewer-page">
    <header class="viewer-header">
        <button mat-icon-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
        </button>
        <h1>AI Setup Review</h1>
    </header>

    <div class="page-disclaimer">
        <mat-icon class="disclaimer-icon">warning</mat-icon>
        <span>The content below was generated by AI and has not been verified.
            You are responsible for reviewing each item before accepting.
            Accepted content becomes part of your fund's data.</span>
    </div>

    @if (detail) {
        @let groups = groupedByAsset(detail.results);

        <mat-accordion multi class="asset-accordion">
            @for (group of groups; track group.assetId) {
                @let pending = pendingCount(group);
                <mat-expansion-panel
                    [expanded]="isExpanded(group.assetId)"
                    (opened)="onPanelOpened(group.assetId)"
                    (closed)="onPanelClosed(group.assetId)">
                    <mat-expansion-panel-header>
                        <mat-panel-title>{{ group.assetName || 'Asset ' + group.assetId }}</mat-panel-title>
                        <mat-panel-description>
                            {{ group.results.length }} item{{ group.results.length === 1 ? '' : 's' }}
                            @if (pending > 0) {
                                <span class="pending-tag">{{ pending }} pending</span>
                            }
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <div class="asset-actions">
                        <div class="asset-disclaimer">
                            Review each item carefully. By accepting, you take responsibility for this content's accuracy.
                        </div>
                        @if (pending > 0) {
                            <button mat-flat-button color="primary" type="button"
                                    (click)="acceptAllForAsset(group); $event.stopPropagation()">
                                Accept all {{ pending }}
                            </button>
                        }
                    </div>

                    @for (result of group.results; track result.resultId) {
                        <div class="result-row">
                            <div class="result-header">
                                <h4>{{ sectionLabel(result.section) }}</h4>
                                <span class="status-badge"
                                      [class.status-accepted]="result.status === STATUS.Accepted"
                                      [class.status-skipped]="result.status === STATUS.Skipped"
                                      [class.status-pending]="result.status === STATUS.PendingReview">
                                    {{ result.status }}
                                </span>
                            </div>

                            <div class="result-content">
                                @switch (result.section) {
                                    @case (SECTION.CompanyDescription) {
                                        <div class="diff-block">
                                            @if (result.currentText) {
                                                <div class="diff-current"><label>Current</label><p>{{ result.currentText }}</p></div>
                                            }
                                            <div class="diff-generated"><label>AI generated</label><p>{{ result.generatedText }}</p></div>
                                        </div>
                                    }
                                    @case (SECTION.EndMarkets) {
                                        <div class="diff-block">
                                            @if (result.currentText) {
                                                <div class="diff-current"><label>Current</label><p>{{ result.currentText }}</p></div>
                                            }
                                            <div class="diff-generated"><label>AI generated</label><p>{{ result.generatedText }}</p></div>
                                        </div>
                                    }
                                    @case (SECTION.Swot) {
                                        <div class="swot-grid">
                                            <div><strong>Strengths</strong><p>{{ result.generatedSwot?.strengths }}</p></div>
                                            <div><strong>Weaknesses</strong><p>{{ result.generatedSwot?.weaknesses }}</p></div>
                                            <div><strong>Opportunities</strong><p>{{ result.generatedSwot?.opportunities }}</p></div>
                                            <div><strong>Threats</strong><p>{{ result.generatedSwot?.threats }}</p></div>
                                        </div>
                                    }
                                    @case (SECTION.GeneralInfo) {
                                        <dl class="info-list">
                                            <dt>Country</dt><dd>{{ result.generatedGeneralInfo?.country }}</dd>
                                            <dt>Activity</dt><dd>{{ result.generatedGeneralInfo?.activityDescription }}</dd>
                                            <dt>Stage</dt><dd>{{ result.generatedGeneralInfo?.stage }}</dd>
                                            <dt>GIC</dt><dd>{{ result.generatedGeneralInfo?.gic }}</dd>
                                            <dt>Currency</dt><dd>{{ result.generatedGeneralInfo?.currency }}</dd>
                                            <dt>Publicly listed</dt><dd>{{ result.generatedGeneralInfo?.publiclyListed }}</dd>
                                        </dl>
                                    }
                                    @case (SECTION.ComparableCompanies) {
                                        <ul class="comparables-list">
                                            @for (c of result.generatedComparables; track c.ticker) {
                                                <li><strong>{{ c.ticker }}</strong> — {{ c.companyName }}
                                                    @if (c.evEbitda) { <span class="muted-tag">EV/EBITDA {{ c.evEbitda }}x</span> }
                                                </li>
                                            }
                                        </ul>
                                    }
                                }
                            </div>

                            @if (result.status === STATUS.PendingReview) {
                                <div class="result-actions">
                                    <small class="inline-disclaimer">By accepting, you confirm you've reviewed this AI-generated content.</small>
                                    @let pending = isPending$(result.resultId) | async;
                                    <button mat-stroked-button type="button" (click)="skip(result)" [disabled]="pending">Skip</button>
                                    <button mat-flat-button color="primary" type="button" (click)="accept(result)" [disabled]="pending">Accept</button>
                                </div>
                            } @else if (result.status === STATUS.Accepted) {
                                <div class="result-meta">
                                    Accepted{{ result.acceptedAt ? ' at ' + (result.acceptedAt | date:'short') : '' }}
                                </div>
                            }
                        </div>
                    }
                </mat-expansion-panel>
            }
        </mat-accordion>
    }
</div>
